import { ref, computed, watch, nextTick } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { createNodeFromTemplate } from '../constants/csmlNodeTemplates';

/**
 * Reactive CSML flow graph state and mutations.
 */

export const MIN_ZOOM = 0.4;
export const MAX_ZOOM = 2.5;
const HISTORY_LIMIT = 100;
const HISTORY_DEBOUNCE_MS = 250;

export function useCsmlFlowGraph() {
  const nodes = ref([]);
  const connections = ref([]);
  const nextId = ref(1);
  const selectedNodeIds = ref([]);
  // The properties panel edits one node at a time, so the single-selection
  // view is only set while exactly one node is selected.
  const selectedNodeId = computed({
    get: () =>
      selectedNodeIds.value.length === 1 ? selectedNodeIds.value[0] : null,
    set: value => {
      selectedNodeIds.value = value == null ? [] : [value];
    },
  });
  const selectedConnection = ref(null);
  const zoom = ref(1);
  const isPanMode = ref(false);
  const canvasOffsetX = ref(0);
  const canvasOffsetY = ref(0);

  const selectedNode = computed(() =>
    nodes.value.find(n => n.id === selectedNodeId.value)
  );

  // Undo/redo: every settled change to the graph is recorded as a snapshot.
  // Edits arrive in bursts (typing, dragging), so recording waits for a short
  // pause; a drag pauses recording entirely and commits once when released.
  const history = ref([]);
  const cursor = ref(-1);
  let restoring = false;
  let paused = false;

  const snapshot = () =>
    JSON.stringify({
      nodes: nodes.value,
      connections: connections.value,
      nextId: nextId.value,
    });

  function commitHistory() {
    if (restoring || paused) return;
    const current = snapshot();
    if (history.value[cursor.value] === current) return;
    const kept = history.value.slice(0, cursor.value + 1);
    kept.push(current);
    history.value = kept.slice(-HISTORY_LIMIT);
    cursor.value = history.value.length - 1;
  }

  const scheduleCommit = useDebounceFn(commitHistory, HISTORY_DEBOUNCE_MS);
  watch([nodes, connections], () => scheduleCommit(), { deep: true });

  // Baseline for dirty checks: the last saved (or freshly loaded) graph
  // snapshot. Stored as the snapshot string so a save can keep the submitted
  // baseline even if the live graph changed while the request was pending.
  const savedSnapshot = ref('');

  function resetHistory() {
    const current = snapshot();
    history.value = [current];
    cursor.value = 0;
    savedSnapshot.value = current;
  }

  // When `explicitSnapshot` is passed, that becomes the saved baseline
  // (what the server accepted). Omit it to treat the live graph as saved.
  function markSaved(explicitSnapshot) {
    commitHistory();
    savedSnapshot.value =
      explicitSnapshot !== undefined ? explicitSnapshot : snapshot();
  }

  function getSnapshot() {
    return snapshot();
  }

  // Compared against the saved snapshot directly rather than through the
  // cursor, so an edit still waiting out the debounce already counts.
  const isGraphDirty = computed(() => savedSnapshot.value !== snapshot());

  function pauseHistory() {
    paused = true;
  }

  function resumeHistory() {
    paused = false;
    commitHistory();
  }

  function restoreHistory(index) {
    const state = JSON.parse(history.value[index]);
    restoring = true;
    nodes.value = state.nodes;
    connections.value = state.connections;
    nextId.value = state.nextId;
    cursor.value = index;
    const ids = new Set(state.nodes.map(node => node.id));
    selectedNodeIds.value = selectedNodeIds.value.filter(id => ids.has(id));
    selectedConnection.value = null;
    nextTick(() => {
      restoring = false;
    });
  }

  function undo() {
    commitHistory();
    if (cursor.value > 0) restoreHistory(cursor.value - 1);
  }

  function redo() {
    if (cursor.value < history.value.length - 1) {
      restoreHistory(cursor.value + 1);
    }
  }

  const canUndo = computed(() => cursor.value > 0);
  const canRedo = computed(() => cursor.value < history.value.length - 1);

  function addNode(type, x = 200, y = 200) {
    const template = createNodeFromTemplate(type);
    if (!template) return null;
    const node = {
      id: nextId.value,
      ...template,
      x,
      y,
    };
    nextId.value += 1;
    nodes.value.push(node);
    return node;
  }

  function deleteNode(id) {
    const target = nodes.value.find(n => n.id === id);
    // The start node is the flow's only entry point - the generator bails
    // out without one, and saving is blocked until it is back.
    if (target?.type === 'start') return;
    nodes.value = nodes.value.filter(n => n.id !== id);
    connections.value = connections.value.filter(
      c => c.fromId !== id && c.toId !== id
    );
    selectedNodeIds.value = selectedNodeIds.value.filter(
      nodeId => nodeId !== id
    );
    if (
      selectedConnection.value &&
      (selectedConnection.value.fromId === id ||
        selectedConnection.value.toId === id)
    ) {
      selectedConnection.value = null;
    }
  }

  function duplicateNode(id) {
    const source = nodes.value.find(n => n.id === id);
    if (!source) return null;
    // A second start node would just be dead weight: generateCSML only ever
    // emits the first one it finds.
    if (source.type === 'start') return null;
    const copy = JSON.parse(JSON.stringify(source));
    copy.id = nextId.value;
    nextId.value += 1;
    copy.x = (source.x || 0) + 40;
    copy.y = (source.y || 0) + 40;
    nodes.value.push(copy);
    selectedNodeId.value = copy.id;
    return copy;
  }

  // Duplicates a group together, keeping the wires that run between its
  // members so a copied branch is still a working branch.
  function duplicateNodes(ids) {
    const copies = new Map();
    ids.forEach(id => {
      const copy = duplicateNode(id);
      if (copy) copies.set(id, copy.id);
    });
    connections.value
      .filter(c => copies.has(c.fromId) && copies.has(c.toId))
      .forEach(c => {
        connections.value.push({
          fromId: copies.get(c.fromId),
          toId: copies.get(c.toId),
          fromPortIdx: c.fromPortIdx,
        });
      });
    selectedNodeIds.value = [...copies.values()];
    return [...copies.values()];
  }

  function selectAll() {
    selectedNodeIds.value = nodes.value.map(node => node.id);
    selectedConnection.value = null;
  }

  // Copy/paste goes through an in-memory clipboard of deep copies, so a node
  // edited or deleted after copying still pastes as it was. Start nodes are
  // never copied - a flow has exactly one.
  let clipboard = null;
  let pasteCount = 0;

  function copySelection() {
    const picked = nodes.value.filter(
      node => selectedNodeIds.value.includes(node.id) && node.type !== 'start'
    );
    if (!picked.length) return false;
    const ids = new Set(picked.map(node => node.id));
    clipboard = JSON.parse(
      JSON.stringify({
        nodes: picked,
        connections: connections.value.filter(
          c => ids.has(c.fromId) && ids.has(c.toId)
        ),
      })
    );
    pasteCount = 0;
    return true;
  }

  function pasteClipboard() {
    if (!clipboard) return [];
    pasteCount += 1;
    const offset = 40 * pasteCount;
    const idMap = new Map();
    clipboard.nodes.forEach(source => {
      const copy = JSON.parse(JSON.stringify(source));
      copy.id = nextId.value;
      nextId.value += 1;
      copy.x = (source.x || 0) + offset;
      copy.y = (source.y || 0) + offset;
      idMap.set(source.id, copy.id);
      nodes.value.push(copy);
    });
    clipboard.connections.forEach(c => {
      connections.value.push({
        fromId: idMap.get(c.fromId),
        toId: idMap.get(c.toId),
        fromPortIdx: c.fromPortIdx,
      });
    });
    selectedNodeIds.value = [...idMap.values()];
    selectedConnection.value = null;
    return [...idMap.values()];
  }

  function deleteNodes(ids) {
    ids.forEach(id => deleteNode(id));
  }

  function addConnection(fromId, toId, fromPortIdx = 0) {
    if (fromId === toId) return null;
    const exists = connections.value.some(
      c =>
        c.fromId === fromId && c.toId === toId && c.fromPortIdx === fromPortIdx
    );
    if (exists) return null;
    // Each out-port can only ever drive a single `goto`, so a second wire
    // from the same port replaces the first rather than coexisting with it
    // silently - CSML has no way to follow two unconditional gotos from one
    // port, so leaving both would mean one of them silently never runs.
    const replaced = connections.value.some(
      c => c.fromId === fromId && c.fromPortIdx === fromPortIdx
    );
    if (replaced) {
      connections.value = connections.value.filter(
        c => !(c.fromId === fromId && c.fromPortIdx === fromPortIdx)
      );
    }
    const connection = { fromId, toId, fromPortIdx };
    connections.value.push(connection);
    return { ...connection, replaced };
  }

  function removeConnection(fromId, toId, fromPortIdx) {
    const before = connections.value.length;
    connections.value = connections.value.filter(c => {
      if (fromPortIdx === undefined) {
        return !(c.fromId === fromId && c.toId === toId);
      }
      return !(
        c.fromId === fromId &&
        c.toId === toId &&
        c.fromPortIdx === fromPortIdx
      );
    });
    if (selectedConnection.value) {
      const sc = selectedConnection.value;
      if (
        sc.fromId === fromId &&
        sc.toId === toId &&
        (fromPortIdx === undefined || sc.fromPortIdx === fromPortIdx)
      ) {
        selectedConnection.value = null;
      }
    }
    return connections.value.length < before;
  }

  function updateNode(id, patch) {
    const node = nodes.value.find(n => n.id === id);
    if (!node) return;
    Object.assign(node, patch);
  }

  function clearCanvas() {
    nodes.value = [];
    connections.value = [];
    nextId.value = 1;
    selectedNodeId.value = null;
    selectedConnection.value = null;
    canvasOffsetX.value = 0;
    canvasOffsetY.value = 0;
  }

  function loadGraph({
    nodes: nextNodes = [],
    connections: nextConnections = [],
    nextId: nextNextId,
  }) {
    nodes.value = nextNodes;
    connections.value = nextConnections;
    if (typeof nextNextId === 'number') {
      nextId.value = nextNextId;
    } else {
      const maxId = nextNodes.reduce((max, n) => Math.max(max, n.id || 0), 0);
      nextId.value = maxId + 1;
    }
    selectedNodeId.value = null;
    selectedConnection.value = null;
  }

  function selectNode(id, additive = false) {
    if (id != null && additive) {
      selectedNodeIds.value = selectedNodeIds.value.includes(id)
        ? selectedNodeIds.value.filter(nodeId => nodeId !== id)
        : [...selectedNodeIds.value, id];
    } else {
      selectedNodeId.value = id;
    }
    selectedConnection.value = null;
  }

  function selectNodes(ids, additive = false) {
    selectedNodeIds.value = additive
      ? [...new Set([...selectedNodeIds.value, ...ids])]
      : ids;
    selectedConnection.value = null;
  }

  function selectConnection(connection) {
    selectedConnection.value = connection;
    selectedNodeId.value = null;
  }

  function setZoom(value) {
    zoom.value = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value));
  }

  return {
    nodes,
    connections,
    nextId,
    selectedNodeId,
    selectedNodeIds,
    selectedConnection,
    selectedNode,
    zoom,
    isPanMode,
    canvasOffsetX,
    canvasOffsetY,
    addNode,
    deleteNode,
    duplicateNode,
    duplicateNodes,
    deleteNodes,
    selectAll,
    copySelection,
    pasteClipboard,
    addConnection,
    removeConnection,
    updateNode,
    clearCanvas,
    loadGraph,
    selectNode,
    selectNodes,
    undo,
    redo,
    canUndo,
    canRedo,
    resetHistory,
    markSaved,
    getSnapshot,
    isGraphDirty,
    pauseHistory,
    resumeHistory,
    selectConnection,
    setZoom,
  };
}
